// SPDX-License-Identifier: GPL-3.0-or-later
//
// Read/write applications.json at <root>/.applify/applications.json.

import { type Manifest } from '../types';

const V2_DIR = '.applify';
const V2_FILE = 'applications.json';

async function readJsonFile<T>(
  dir: FileSystemDirectoryHandle,
  name: string,
): Promise<T | null> {
  let fileHandle: FileSystemFileHandle;
  try {
    fileHandle = await dir.getFileHandle(name);
  } catch {
    return null;
  }
  const file = await fileHandle.getFile();
  const text = await file.text();
  if (!text.trim()) return null;
  return JSON.parse(text) as T;
}

async function getSubDir(
  parent: FileSystemDirectoryHandle,
  name: string,
  options?: { create?: boolean },
): Promise<FileSystemDirectoryHandle | null> {
  try {
    return await parent.getDirectoryHandle(name, options);
  } catch {
    return null;
  }
}

export async function readManifest(
  root: FileSystemDirectoryHandle,
): Promise<Manifest | null> {
  const applifyDir = await getSubDir(root, V2_DIR);
  if (!applifyDir) return null;
  const v2 = await readJsonFile<Manifest>(applifyDir, V2_FILE);
  if (v2 && v2.schemaVersion === 2) return v2;
  return null;
}

// ----- Single-flight write queue -----
//
// File System Access does not serialize concurrent writes against the same
// handle. We serialize ourselves: only one writeManifest call is in-flight at
// a time; subsequent calls coalesce — the latest enqueued manifest is what gets
// written next, so a flurry of UI mutations collapses into one disk write.

let writingNow: Promise<void> | null = null;
let nextWrite: { manifest: Manifest; root: FileSystemDirectoryHandle } | null = null;

async function doWrite(
  root: FileSystemDirectoryHandle,
  manifest: Manifest,
): Promise<void> {
  const dir = await getSubDir(root, V2_DIR, { create: true });
  if (!dir) throw new Error(`Could not open or create ${V2_DIR}/`);

  const fileHandle = await dir.getFileHandle(V2_FILE, { create: true });
  const writable = await fileHandle.createWritable();
  try {
    await writable.write(JSON.stringify(manifest, null, 2));
  } finally {
    await writable.close();
  }
}

export async function writeManifest(
  root: FileSystemDirectoryHandle,
  manifest: Manifest,
): Promise<void> {
  nextWrite = { root, manifest };
  if (writingNow) {
    await writingNow;
    return;
  }
  writingNow = (async () => {
    try {
      while (nextWrite) {
        const pending = nextWrite;
        nextWrite = null;
        await doWrite(pending.root, pending.manifest);
      }
    } finally {
      writingNow = null;
    }
  })();
  await writingNow;
}
